/**
 * Indonesian-aware formatters.
 *
 * The product surface defaults to Bahasa Indonesia phrasing; English
 * is reserved for technical terms. Number + date formatters use
 * ``id-ID`` locale (dot thousand separator, 24-hour clock, "WIB"
 * zone awareness handled by callers).
 */

const ID_LOCALE = "id-ID";

export function formatNumberID(value: number): string {
  return new Intl.NumberFormat(ID_LOCALE).format(value);
}

export function formatCompactNumberID(value: number): string {
  return new Intl.NumberFormat(ID_LOCALE, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

export function formatPercentID(value: number, fractionDigits = 0): string {
  return new Intl.NumberFormat(ID_LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

export function formatDateID(input: string | Date): string {
  const d = typeof input === "string" ? new Date(input) : input;
  return new Intl.DateTimeFormat(ID_LOCALE, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/**
 * "240ms" / "1.42s". Switches to seconds with two decimals once
 * the value crosses one full second. Negative / non-finite
 * inputs fall back to an em-dash so callers don't need to guard.
 */
export function formatMilliseconds(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * "$0.0041" for per-request cost (4 decimals); "$12.40" once the
 * value is large enough that 4 decimals would look like noise. Accepts
 * the Decimal-as-string the BE returns plus plain numbers.
 */
export function formatCostUSD(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";
  const digits = Math.abs(n) >= 1 ? 2 : 4;
  return `$${n.toFixed(digits)}`;
}

/**
 * "Rp 64" / "Rp 192.200" — whole rupiah with id-ID thousand separator.
 * IDR has no cents in display per Indonesian convention.
 */
export function formatCostIDR(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";
  return `Rp ${formatNumberID(Math.round(n))}`;
}

/**
 * "5 menit lalu", "2 jam lalu", "kemarin", "3 hari lalu", ...
 *
 * Falls back to a full-date label once we cross 7 days. Keeps the
 * recent-activity feed scannable without each row carrying a full
 * timestamp.
 */
export function formatRelativeTimeID(input: string | Date): string {
  const d = typeof input === "string" ? new Date(input) : input;
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);

  if (seconds < 60) return "baru saja";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} menit lalu`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} jam lalu`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "kemarin";
  if (days < 7) return `${days} hari lalu`;
  return formatDateID(d);
}
